/* oxlint-disable sorare/no-detached-fragments */
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { isType, isTypes, isTypesFn } from '@sorare/core/src/lib/gql';

import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import type {
  TaskClaimedDialogs_anyRewardInterface,
  TaskClaimedDialogs_taskInterface,
} from './__generated__/index.graphql.ts';
import type {
  ClaimTaskWithPackMutation,
  ClaimTaskWithPackMutationVariables,
} from './__generated__/useTaskClaimedDialogs.graphql.ts';
import { TaskClaimedDialogs } from './index.tsx';

const CLAIM_TASK_WITH_PACK_MUTATION = gql`
  mutation ClaimTaskWithPackMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
      }
      rewards {
        id
        ...TaskClaimedDialogs_anyRewardInterface
      }

      errors {
        message
        code
      }
    }
  }
  ${TaskClaimedDialogs.fragments.anyRewardInterface}
` as TypedDocumentNode<
  ClaimTaskWithPackMutation,
  ClaimTaskWithPackMutationVariables
>;

// Gems, XP and essences are claimed without a dialog. Anything else, or a
// task needing user input or showing extra steps, keeps the dialog.
const canClaimSilently = (task: TaskClaimedDialogs_taskInterface) =>
  !task.alternativeRewardConfigs?.length &&
  !task.ladderContributions.length &&
  !isTypes(task, ['DecisivePlayerPickerTask', 'ThresholdsStreakTask']) &&
  task.rewardConfigs.length > 0 &&
  task.rewardConfigs.every(
    isTypesFn(['InGameCurrencyRewardConfig', 'CardShardRewardConfig'])
  );

export const useTaskClaimedDialogs = () => {
  const { sport } = useSportContext();
  const [claimedTask, setClaimedTask] =
    useState<TaskClaimedDialogs_taskInterface | null>(null);
  const [rewardDialogTask, setRewardDialogTask] =
    useState<TaskClaimedDialogs_taskInterface | null>(null);
  const [revealPackCardsDialogReward, setRevealPackCardsDialogReward] =
    useState<TaskClaimedDialogs_anyRewardInterface | null>(null);
  const [pickerDialogTask, setPickerDialogTask] =
    useState<TaskClaimedDialogs_taskInterface | null>(null);

  const [claimTaskWithPack, { loading: loadingClaimTaskWithPack }] =
    useMutation(CLAIM_TASK_WITH_PACK_MUTATION, {
      showErrorsWithSnackNotification: true,
      // See useClaimTask: refetch the active queries the claim actually affects
      // (via the written task aasmState) instead of a manual per-surface prop.
      onQueryUpdated: async observableQuery => observableQuery.refetch(),
    });
  const [claimTask, { loading: loadingClaimTask }] = useClaimTask();

  const onClaim = async (task: TaskClaimedDialogs_taskInterface) => {
    setClaimedTask(task);

    const firstRewardConfig = task.rewardConfigs.at(0);
    const hasCardPackReward = isType(
      firstRewardConfig,
      'CardPacksRewardConfig'
    );

    if (hasCardPackReward) {
      const { data: dataClaimTask } = await claimTaskWithPack({
        variables: {
          taskId: task.id,
        },
      });
      const reward = dataClaimTask?.claimTask?.rewards?.at(0);
      if (reward) {
        setRevealPackCardsDialogReward(reward);
      }
    } else if (canClaimSilently(task)) {
      await claimTask({ variables: { taskId: task.id, sport } });
    } else {
      setRewardDialogTask(task);
    }
  };

  return {
    onClaim,
    loadingTaskId:
      (loadingClaimTaskWithPack || loadingClaimTask) && claimedTask?.id,
    rewardDialogTask,
    setRewardDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    pickerDialogTask,
    setPickerDialogTask,
  };
};
